// src/Dashboard/pages/user/Inscription/hooks/useInscriptionDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAdherentsByUser } from "../../../../../services/adherentServices";
import { getDossierInscription } from "../../../../../services/dossierServices";
import { getGroupName } from "../../../../../services/groupServices";
import { getAllSegments } from "../../../../../services/segmentDossierServices";
import { getSettings } from "../../../../../services/settingsServices";
import { getReglementInscriptionByIdAndSaison } from "../../../../../services/reglementinscriptionServices";
import { useUser } from "../../../../../pages/Home/UserContext";

import {
  computeDossierSummary,
  normalizeText,
  unwrapSettingsRow,
} from "../utils/inscriptionUtils";

export function useInscriptionDashboard() {
  const { user } = useUser();
  const userId = user?.userId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [adherents, setAdherents] = useState([]);
  const [dossiers, setDossiers] = useState({});
  const [reglements, setReglements] = useState({});
  const [groupNames, setGroupNames] = useState({});
  const [segments, setSegments] = useState([]);

  const [settings, setSettings] = useState({});
  const [activeSeason, setActiveSeason] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [seasonFilter, setSeasonFilter] = useState("all");

  const [drawer, setDrawer] = useState(null);
  const [selected, setSelected] = useState(null);

  const safeGetDossier = useCallback(async (adherentId) => {
    try {
      return await getDossierInscription(adherentId);
    } catch (err) {
      const status = err?.response?.status ?? err?.status;

      if (status === 404) return null;

      console.error("getDossierInscription failed:", err);
      return null;
    }
  }, []);

  const safeGetReglement = useCallback(async (adherentId, season) => {
    if (!season) return null;

    try {
      return await getReglementInscriptionByIdAndSaison(adherentId, season);
    } catch (err) {
      console.error("getReglementInscriptionByIdAndSaison failed:", err);
      return null;
    }
  }, []);

  const loadAll = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      setError("Utilisateur non identifié.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const [settingsResponse, segmentsResponse, adherentsResponse] =
        await Promise.all([
          getSettings(),
          getAllSegments(),
          getAdherentsByUser(userId),
        ]);

      const cleanSettings = unwrapSettingsRow(settingsResponse);
      const season = cleanSettings?.saison_active || "";

      const adherentsList = Array.isArray(adherentsResponse)
        ? adherentsResponse
        : [];

      setSettings(cleanSettings || {});
      setActiveSeason(season);
      setSegments(Array.isArray(segmentsResponse) ? segmentsResponse : []);
      setAdherents(adherentsList);

      const dossiersResults = await Promise.all(
        adherentsList.map((adherent) => safeGetDossier(adherent.id))
      );

      const groupsResults = await Promise.all(
        adherentsList.map((adherent) =>
          getGroupName(adherent.id).catch(() => [])
        )
      );

      const reglementsResults = await Promise.all(
        adherentsList.map((adherent) =>
          safeGetReglement(adherent.id, season)
        )
      );

      setDossiers(
        adherentsList.reduce((acc, adherent, index) => {
          acc[adherent.id] = dossiersResults[index];
          return acc;
        }, {})
      );

      setGroupNames(
        adherentsList.reduce((acc, adherent, index) => {
          const value = groupsResults[index];
          acc[adherent.id] = Array.isArray(value) ? value : [];
          return acc;
        }, {})
      );

      setReglements(
        adherentsList.reduce((acc, adherent, index) => {
          acc[adherent.id] = reglementsResults[index];
          return acc;
        }, {})
      );
    } catch (err) {
      console.error("Erreur chargement dossier inscription:", err);
      setError("Impossible de charger les dossiers d’inscription.");
      toast.error("Erreur lors du chargement des dossiers d’inscription.");
    } finally {
      setLoading(false);
    }
  }, [safeGetDossier, safeGetReglement, userId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const refreshDossier = useCallback(
    async (adherentId) => {
      const [dossier, reglement] = await Promise.all([
        safeGetDossier(adherentId),
        safeGetReglement(adherentId, activeSeason),
      ]);

      setDossiers((previous) => ({
        ...previous,
        [adherentId]: dossier,
      }));

      setReglements((previous) => ({
        ...previous,
        [adherentId]: reglement,
      }));
    },
    [activeSeason, safeGetDossier, safeGetReglement]
  );

  const refreshAdherent = useCallback(
    async (adherentId) => {
      if (!userId) return;

      try {
        const list = await getAdherentsByUser(userId);
        const cleanList = Array.isArray(list) ? list : [];

        setAdherents(cleanList);

        const updated = cleanList.find(
          (adherent) => String(adherent.id) === String(adherentId)
        );

        if (updated) {
          setSelected(updated);
          await refreshDossier(updated.id);

          const groups = await getGroupName(updated.id).catch(() => []);
          setGroupNames((previous) => ({
            ...previous,
            [updated.id]: Array.isArray(groups) ? groups : [],
          }));
        }
      } catch (err) {
        console.error("refreshAdherent failed:", err);
        toast.error("Impossible de rafraîchir l’adhérent.");
      }
    },
    [refreshDossier, userId]
  );

  const summaries = useMemo(() => {
    return adherents.reduce((acc, adherent) => {
      acc[adherent.id] = computeDossierSummary({
        adherent,
        dossier: dossiers[adherent.id],
        reglement: reglements[adherent.id],
        activeSeason,
      });

      return acc;
    }, {});
  }, [activeSeason, adherents, dossiers, reglements]);

  const filteredAdherents = useMemo(() => {
    const q = normalizeText(search);

    return adherents.filter((adherent) => {
      const summary = summaries[adherent.id];

      const matchSearch = !q
        ? true
        : normalizeText(
            `${adherent.nom || ""} ${adherent.prenom || ""} ${
              adherent.licence_number || ""
            }`
          ).includes(q);

      const matchStatus =
        statusFilter === "all" ? true : summary?.globalStatus === statusFilter;

      const matchSeason =
        seasonFilter === "all"
          ? true
          : seasonFilter === "current"
          ? summary?.isCurrentSeason
          : !summary?.isCurrentSeason;

      return matchSearch && matchStatus && matchSeason;
    });
  }, [adherents, search, seasonFilter, statusFilter, summaries]);

  const stats = useMemo(() => {
    const values = Object.values(summaries);

    return {
      total: adherents.length,
      upToDate: values.filter((item) => item.globalStatus === "up_to_date")
        .length,
      missing: values.filter((item) => item.globalStatus === "missing")
        .length,
      waiting: values.filter((item) => item.globalStatus === "waiting").length,
      renewal: values.filter((item) => item.globalStatus === "renewal").length,
      noDossier: values.filter((item) => item.globalStatus === "no_dossier")
        .length,
    };
  }, [adherents.length, summaries]);

  const openDossierDrawer = (adherent) => {
    setSelected(adherent);
    setDrawer("dossier");
  };

  const openDocumentsDrawer = (adherent) => {
    setSelected(adherent);
    setDrawer("documents");
  };

  const openRenewalDrawer = (adherent) => {
    setSelected(adherent);
    setDrawer("renewal");
  };

  const openTrialDrawer = (adherent) => {
    setSelected(adherent);
    setDrawer("trial");
  };

  const closeDrawer = () => {
    setDrawer(null);
  };

  return {
    loading,
    error,

    user,
    adherents,
    filteredAdherents,
    dossiers,
    reglements,
    groupNames,
    segments,
    settings,
    activeSeason,

    summaries,
    stats,

    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    seasonFilter,
    setSeasonFilter,

    selected,
    drawer,
    openDossierDrawer,
    openDocumentsDrawer,
    openRenewalDrawer,
    openTrialDrawer,
    closeDrawer,

    refreshAll: loadAll,
    refreshAdherent,
    refreshDossier,
  };
}